import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Check, Eye, LockKeyhole, Palette, Save, ShieldCheck, SlidersHorizontal, Sparkles, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { catalogueFor, type MembershipAudience } from "@/lib/entitlements";
import { getOwnerMembershipConfiguration, getOwnerMembershipTestModes, setOwnerMembershipTestMode, updateOwnerMembershipPlan } from "@/lib/owner-memberships.functions";

export const Route = createFileRoute("/_authenticated/owner/memberships")({
  head: () => ({ meta: [
    { title: "Memberships & Experience — Shields Control Centre" },
    { name: "description", content: "Owner-only membership, entitlement and personalisation control." },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: OwnerMemberships,
});

type Plan = {
  slug: string;
  audience: MembershipAudience;
  name: string;
  description?: string | null;
  price_pence_monthly: number;
  billing_interval?: "month" | "year" | null;
  active: boolean;
  visible?: boolean | null;
  entitlement_keys?: string[] | null;
  feature_limits?: Record<string, string | number | boolean | null> | null;
  upgrade_message?: string | null;
  sort_order?: number | null;
};

const SECTIONS = [
  ["memberships", "Memberships"],
  ["entitlements", "Feature entitlements"],
  ["personalisation", "Personalisation"],
  ["ai", "AI personalisation"],
  ["locked", "Locked features"],
  ["preview", "Membership preview"],
] as const;

function OwnerMemberships() {
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: ["owner-membership-configuration"],
    queryFn: () => getOwnerMembershipConfiguration() as Promise<Plan[]>,
    retry: false,
  });
  const testModesQuery = useQuery({
    queryKey: ["owner-membership-test-modes"],
    queryFn: () => getOwnerMembershipTestModes() as Promise<Array<{ audience: MembershipAudience; plan_slug: string }>>,
    retry: false,
  });
  const testModeMutation = useMutation({
    mutationFn: (input: { audience: MembershipAudience; planSlug: string | null }) => setOwnerMembershipTestMode({ data: input }),
    onSuccess: async (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      toast.success("Owner test experience updated");
      await queryClient.invalidateQueries({ queryKey: ["owner-membership-test-modes"] });
      await queryClient.invalidateQueries({ queryKey: ["personalisation"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Could not update test experience"),
  });
  const [audience, setAudience] = useState<MembershipAudience>("customer");
  const [section, setSection] = useState<(typeof SECTIONS)[number][0]>("memberships");
  const plans = useMemo(
    () => (plansQuery.data ?? []).filter((plan) => plan.audience === audience).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [plansQuery.data, audience],
  );
  const [selectedSlug, setSelectedSlug] = useState("");
  const selected = plans.find((plan) => plan.slug === selectedSlug) ?? plans[0];

  useEffect(() => {
    if (!plans.some((plan) => plan.slug === selectedSlug)) setSelectedSlug(plans[0]?.slug ?? "");
  }, [plans, selectedSlug]);

  if (plansQuery.isLoading) return <ConsoleState text="Loading membership controls…" />;
  if (plansQuery.error) return <ConsoleState text="Owner authorisation or membership configuration could not be loaded." danger />;

  return (
    <div className="min-h-screen bg-background pb-24 text-foreground">
      <AppHeader workspace="owner" />
      <main className="mx-auto w-full max-w-[1500px] px-4 py-5 md:px-7">
        <section className="border border-border/80 bg-card/75 p-5 shadow-2xl shadow-black/30 md:p-7">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="technical-label">SHIELDS CONTROL CENTRE · PRODUCT</p>
              <h1 className="mt-2 text-2xl font-black tracking-tight md:text-4xl">Memberships &amp; Experience</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                Configure product access without mixing memberships, marketplace Credits or customer GBP payments.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-px overflow-hidden border border-border bg-border text-center">
              <Metric label="PLANS" value={plansQuery.data?.length ?? 0} />
              <Metric label="ACTIVE" value={(plansQuery.data ?? []).filter((p) => p.active).length} />
              <Metric label="VISIBLE" value={(plansQuery.data ?? []).filter((p) => p.visible !== false).length} />
            </div>
          </div>
        </section>

        <div className="mt-4 grid gap-4 xl:grid-cols-[230px_minmax(0,1fr)]">
          <aside className="border border-border bg-card/65 p-3">
            <p className="technical-label px-2 py-2">MEMBERSHIPS &amp; EXPERIENCE</p>
            <nav className="mt-2 grid gap-1" aria-label="Membership controls">
              {SECTIONS.map(([key, label]) => (
                <button key={key} onClick={() => setSection(key)} className={"flex min-h-10 items-center justify-between border px-3 text-left text-xs font-bold transition " + (section === key ? "border-primary/60 bg-primary/10 text-primary" : "border-transparent text-muted-foreground hover:border-border hover:bg-secondary/40 hover:text-foreground")}>
                  {label}<span aria-hidden>→</span>
                </button>
              ))}
            </nav>
            <div className="mt-5 border-t border-border pt-4">
              <p className="technical-label px-2">EXPERIENCE</p>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {(["customer", "partner"] as const).map((item) => (
                  <button key={item} onClick={() => setAudience(item)} className={"min-h-10 border px-2 text-[11px] font-black uppercase tracking-wider " + (audience === item ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground")}>
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section className="min-w-0 border border-border bg-card/55 p-4 md:p-6">
            <SectionHeading section={section} audience={audience} />
            <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
              {plans.map((plan) => (
                <button key={plan.slug} onClick={() => setSelectedSlug(plan.slug)} className={"min-w-36 border px-4 py-3 text-left " + (selected?.slug === plan.slug ? "border-primary/70 bg-primary/10" : "border-border bg-background/40")}>
                  <span className="technical-label">{plan.active ? "ACTIVE" : "INACTIVE"}</span>
                  <strong className="mt-1 block text-sm">{plan.name}</strong>
                  <span className="mt-1 block text-xs tabular-nums text-muted-foreground">£{(plan.price_pence_monthly / 100).toFixed(2)} / {plan.billing_interval ?? "month"}</span>
                </button>
              ))}
            </div>
            {selected ? (
              section === "preview"
                ? <MembershipPreview
                    plan={selected}
                    active={testModesQuery.data?.some((item) => item.audience === selected.audience && item.plan_slug === selected.slug) ?? false}
                    busy={testModeMutation.isPending}
                    onToggle={() => testModeMutation.mutate({ audience: selected.audience, planSlug: (testModesQuery.data?.some((item) => item.audience === selected.audience && item.plan_slug === selected.slug) ?? false) ? null : selected.slug })}
                  />
                : <PlanEditor key={selected.slug + section} plan={selected} section={section} onSaved={() => queryClient.invalidateQueries({ queryKey: ["owner-membership-configuration"] })} />
            ) : (
              <div className="mt-6 border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No {audience} membership plans are configured.</div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

function SectionHeading({ section, audience }: { section: string; audience: MembershipAudience }) {
  const icons = { memberships: ShieldCheck, entitlements: SlidersHorizontal, personalisation: Palette, ai: Bot, locked: LockKeyhole, preview: Eye };
  const Icon = icons[section as keyof typeof icons] ?? ShieldCheck;
  return (
    <div className="flex items-start gap-3 border-b border-border pb-4">
      <span className="flex h-9 w-9 items-center justify-center border border-primary/40 bg-primary/10 text-primary"><Icon className="h-4 w-4" aria-hidden /></span>
      <div><p className="technical-label">{audience.toUpperCase()} EXPERIENCE</p><h2 className="mt-1 text-lg font-extrabold">{SECTIONS.find(([key]) => key === section)?.[1]}</h2></div>
    </div>
  );
}

function PlanEditor({ plan, section, onSaved }: { plan: Plan; section: string; onSaved: () => void }) {
  const catalogue = catalogueFor(plan.audience);
  const [draft, setDraft] = useState({
    name: plan.name,
    description: plan.description ?? "",
    pricePenceMonthly: plan.price_pence_monthly,
    billingInterval: plan.billing_interval ?? "month",
    active: plan.active,
    visible: plan.visible !== false,
    entitlementKeys: plan.entitlement_keys ?? [],
    featureLimits: plan.feature_limits ?? {},
    upgradeMessage: plan.upgrade_message ?? "",
  });
  const mutation = useMutation({
    mutationFn: () => updateOwnerMembershipPlan({ data: { slug: plan.slug, ...draft } }),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      toast.success(plan.name + " updated");
      onSaved();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Update failed"),
  });

  const visibleEntitlements = catalogue.filter((item) => {
    if (section === "personalisation") return item.group === "personalisation";
    if (section === "ai") return item.group === "ai";
    if (section === "locked") return true;
    if (section === "entitlements") return true;
    return false;
  });

  function toggle(key: string) {
    setDraft((current) => ({ ...current, entitlementKeys: current.entitlementKeys.includes(key) ? current.entitlementKeys.filter((item) => item !== key) : [...current.entitlementKeys, key] }));
  }

  return (
    <div className="mt-5">
      {section === "memberships" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="MEMBERSHIP NAME"><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="owner-field" /></Field>
          <Field label="MONTHLY PRICE (PENCE)"><input type="number" min={0} value={draft.pricePenceMonthly} onChange={(e) => setDraft({ ...draft, pricePenceMonthly: Number(e.target.value) })} className="owner-field tabular-nums" /></Field>
          <Field label="BILLING INTERVAL"><select value={draft.billingInterval} onChange={(e) => setDraft({ ...draft, billingInterval: e.target.value as "month" | "year" })} className="owner-field"><option value="month">Monthly</option><option value="year">Yearly</option></select></Field>
          <Field label="PLAN STATUS"><div className="grid grid-cols-2 gap-2"><Toggle checked={draft.active} onClick={() => setDraft({ ...draft, active: !draft.active })} label="Active" /><Toggle checked={draft.visible} onClick={() => setDraft({ ...draft, visible: !draft.visible })} label="Visible" /></div></Field>
          <Field label="DESCRIPTION" wide><textarea rows={4} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="owner-field resize-y" /></Field>
          <Field label="UPGRADE MESSAGE" wide><textarea rows={3} value={draft.upgradeMessage} onChange={(e) => setDraft({ ...draft, upgradeMessage: e.target.value })} className="owner-field resize-y" /></Field>
        </div>
      ) : (
        <div className="grid gap-2 lg:grid-cols-2">
          {visibleEntitlements.map((item) => {
            const enabled = draft.entitlementKeys.includes(item.key);
            return (
              <button key={item.key} onClick={() => toggle(item.key)} className={"flex min-h-24 items-start gap-3 border p-4 text-left transition " + (enabled ? "border-primary/60 bg-primary/10" : "border-border bg-background/35")}>
                <span className={"mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border " + (enabled ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/50")}>{enabled && <Check className="h-3.5 w-3.5" aria-hidden />}</span>
                <span><strong className="block text-sm">{item.label}</strong><small className="mt-1 block leading-5 text-muted-foreground">{item.description}</small><code className="mt-2 block text-[10px] text-primary/80">{item.key}</code></span>
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-4 border-t border-border pt-4">
        <p className="text-xs text-muted-foreground">Changes affect effective access and are recorded in the Owner audit log.</p>
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="min-h-11 min-w-32">
          <Save className="mr-2 h-4 w-4" aria-hidden />{mutation.isPending ? "Saving…" : "Save plan"}
        </Button>
      </div>
    </div>
  );
}

function MembershipPreview({ plan, active, busy, onToggle }: { plan: Plan; active: boolean; busy: boolean; onToggle: () => void }) {
  const catalogue = catalogueFor(plan.audience);
  const enabled = new Set(plan.entitlement_keys ?? []);
  return (
    <div className="mt-5 grid gap-4 lg:grid-cols-[0.8fr_1.2fr]">
      <section className="border border-primary/40 bg-primary/5 p-5">
        <p className="technical-label">PREVIEW AS {plan.audience.toUpperCase()}</p>
        <h3 className="mt-3 text-2xl font-black">{plan.name}</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{plan.description || "No plan description configured."}</p>
        <strong className="mt-6 block text-3xl tabular-nums">£{(plan.price_pence_monthly / 100).toFixed(2)}</strong>
        <span className="text-xs text-muted-foreground">per {plan.billing_interval ?? "month"}</span>
        <div className="mt-6 status-chip status-chip-success"><Eye className="h-3.5 w-3.5" aria-hidden /> Owner-only test · no billing change</div>
        <Button onClick={onToggle} disabled={busy} className="mt-4 min-h-12 w-full">
          {busy ? "Applying…" : active ? "Stop using this test membership" : `Use ${plan.name} in my test experience`}
        </Button>
        <p className="mt-3 text-[11px] leading-5 text-muted-foreground">
          This changes only your Owner account’s effective feature access for testing. It does not impersonate a user, create a subscription or bypass non-membership security.
        </p>
      </section>
      <section className="border border-border bg-background/40 p-5">
        <p className="technical-label">EFFECTIVE ACCESS</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {catalogue.map((item) => (
            <div key={item.key} className={"border p-3 " + (enabled.has(item.key) ? "border-success/35 bg-success/5" : "border-border opacity-65")}>
              <div className="flex items-center gap-2">{enabled.has(item.key) ? <Check className="h-4 w-4 text-success" /> : <LockKeyhole className="h-4 w-4 text-muted-foreground" />}<strong className="text-xs">{item.label}</strong></div>
              <p className="mt-2 text-[11px] leading-5 text-muted-foreground">{enabled.has(item.key) ? "Available in this membership." : "Visible as locked where product discovery is appropriate."}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div className="min-w-24 bg-background/70 px-4 py-3"><span className="technical-label">{label}</span><strong className="mt-1 block text-xl tabular-nums">{String(value).padStart(2, "0")}</strong></div>;
}
function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={wide ? "lg:col-span-2" : ""}><span className="technical-label mb-2 block">{label}</span>{children}</label>;
}
function Toggle({ checked, onClick, label }: { checked: boolean; onClick: () => void; label: string }) {
  return <button type="button" onClick={onClick} className={"flex min-h-11 items-center justify-between border px-3 text-xs font-bold " + (checked ? "border-success/50 bg-success/10 text-success" : "border-border text-muted-foreground")}>{label}<span className={"h-2 w-2 rounded-full " + (checked ? "bg-success" : "bg-muted-foreground/40")} /></button>;
}
function ConsoleState({ text, danger = false }: { text: string; danger?: boolean }) {
  return <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center text-sm text-foreground"><div className={"border p-6 " + (danger ? "border-destructive/50" : "border-border")}><Users className="mx-auto mb-3 h-5 w-5 text-primary" />{text}</div></div>;
}
